import React, { useCallback, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import AddCoverImageIcon from '@wix/da-ds/pkg/Icons/24x24/AddCoverImage';
import EditIcon from '@wix/da-ds/pkg/Icons/24x24/Edit';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import {
  useMMDispatchActions,
  useMMSelector,
} from '../../../_hocs/withOwnReduxStore';
import DeviationDumbView from '../../../../DeviationViews/Dumbo';
import IconButtonWithBackground from '@wix/da-ds/pkg/Buttons/IconButton/IconButtonWithBackground';
import {
  getFolderNameValidationErrorMessage,
  MAX_TITLE_LENGTH,
} from '../../../../utils/isValidFolderName';
import { useGallections } from '../../../_hocs/withGallections';
import {
  setGallectionName,
  setGallectionDescription,
  setGallectionPrice,
} from '../../../actions';
import { useCurrentGallection } from '../../../_hocs/withCurrentGallection';
import { useCurrentSubfolder } from '../../../_hocs/withCurrentSubfolder';
import {
  getCanChangeGallectionDescription,
  getCanRenameGallection,
  getCanSetGallectionCover,
} from '../../../helpers';
import FolderRibbon from '../../../../Primitives/FolderRibbon';
import { normalizePremiumFolderData } from '../../../../utils/normalizePremiumFolderData';
import GallectionCoverSelector from '../../../_partials/GallectionCoverSelector';
import FolderCoverInfo from '../../../../FolderCoverInfo';
import { dollarsToPoints } from '../../../../utils/points';

import PriceField from './_partials/PriceField';
import useIsSellableAsPremiumGallery from '../../../../utils/hooks/useIsSellableAsPremiumGallery';
import { getGallectionById, getSubfoldersByParentId } from '../../../selectors';
import PremiumContent from './_partials/PremiumContent';
import FormElement from '@wix/da-ds/pkg/formControls/layouts/FormElement';
import InputLabelText from '@wix/da-ds/pkg/formControls/lego/InputLabelText';
import { PapiEditorText } from '@wix/da-papi-types';
import GallectionEditor from '../../../../GallectionEditor';
import { EditorApi } from '@wix/da-editor-tiptap/pkg/components/Editor/types';
import LegacyDescriptionEditor from '../../../../DescriptionEditor/lego/LegacyDescriptionEditor';

import gallectionSidebarStyles from './GallectionSidebar.scss';

export interface Props {}

export const GallectionSidebar: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { gallections } = useGallections();
  const { currentGallection: currentGallectionFolder } = useCurrentGallection();
  const { currentSubfolder } = useCurrentSubfolder();
  const currentGallection = currentSubfolder || currentGallectionFolder!;
  const [isShowingMediaSelector, setIsShowingMediaSelector] = useState(false);

  const gallectionMM = useMMSelector(state =>
    getGallectionById(state, currentGallection?.folderId)
  );

  const subfoldersMM = useMMSelector(state =>
    getSubfoldersByParentId(state, currentGallection?.folderId)
  );

  const showSellAsPremiumGalleryButton = useIsSellableAsPremiumGallery(
    gallectionMM,
    subfoldersMM.length > 0
  );

  const showMediaSelector = useCallback(
    () => setIsShowingMediaSelector(true),
    [setIsShowingMediaSelector]
  );
  const hideMediaSelector = useCallback(
    () => setIsShowingMediaSelector(false),
    [setIsShowingMediaSelector]
  );
  const [nameErrorMessage, setNameErrorMessage] = useState<string | null>();
  const actions = useMMDispatchActions({
    setGallectionName,
    setGallectionDescription,
    setGallectionPrice,
  });

  const existingNames = useMemo(
    () =>
      gallections
        .filter(
          gallection => gallection.folderId !== currentGallection.folderId
        )
        .map(gallection => gallection.name),
    [gallections, currentGallection.folderId]
  );

  const onChangeGallectionName = useCallback(
    name => {
      const message = getFolderNameValidationErrorMessage(
        name,
        t,
        existingNames,
        currentGallection.type === 'gallery'
      );
      setNameErrorMessage(message);
      actions.setGallectionName({
        folderId: currentGallection.folderId,
        name,
      });
    },
    [
      currentGallection.folderId,
      currentGallection.type,
      actions,
      existingNames,
      setNameErrorMessage,
      t,
    ]
  );

  const editorApi = useRef<EditorApi>(undefined);
  const onChangeGallectionDescriptionTiptap = useCallback(() => {
    // No way to not do this on every key...
    const description = JSON.stringify(editorApi.current?.getStateForSave());

    actions.setGallectionDescription({
      folderId: currentGallection.folderId,
      description,
    });
  }, [currentGallection.folderId, actions]);

  const onChangeGallectionPrice = useCallback(
    (value: string, error?: string) => {
      if (!value || !!error) {
        return;
      }
      actions.setGallectionPrice({
        folderId: currentGallection.folderId,
        pointsPrice: dollarsToPoints(+value),
      });
    },
    [currentGallection.folderId, actions]
  );

  const { isPaidFolder, isWatchToAccess } = normalizePremiumFolderData(
    currentGallection.premiumFolderData
  );

  const renderGallectionCoverBlock = () => {
    const gallectionThumb = currentGallection.thumb;
    const { isPremium: isThumbPremium } = normalizePremiumFolderData(
      gallectionThumb?.premiumFolderData
    );

    const canSetGallectionCover = getCanSetGallectionCover(currentGallection);

    return (
      <div className={gallectionSidebarStyles['cover-wrapper']}>
        {gallectionThumb ? (
          <DeviationDumbView
            useCoverMode
            width={parseInt(gallectionSidebarStyles['width'])}
            height={parseInt(
              gallectionSidebarStyles[
                isPaidFolder ? 'cover-height-paid-folder' : 'cover-height'
              ]
            )}
            deviation={gallectionThumb}
          />
        ) : (
          <div className={gallectionSidebarStyles['no-cover']} />
        )}
        {canSetGallectionCover && !gallectionThumb && (
          <button
            className={gallectionSidebarStyles['cover-button-add']}
            onClick={showMediaSelector}
          >
            <AddCoverImageIcon
              className={gallectionSidebarStyles['cover-button-icon']}
            />
            <br />
            {t('mediapopup.setGallectionCover')}
          </button>
        )}
        {canSetGallectionCover && gallectionThumb && (
          <div className={gallectionSidebarStyles['cover-buttons-wrapper']}>
            {isThumbPremium && <FolderCoverInfo />}
            <IconButtonWithBackground
              size="small"
              aria-label={t('common.edit')}
              icon={EditIcon}
              className={gallectionSidebarStyles['cover-button-edit']}
              onClick={showMediaSelector}
            />
          </div>
        )}
      </div>
    );
  };

  const renderSubscribersCount = () => (
    <div className={gallectionSidebarStyles['subscribers-counter']}>
      <span>{currentGallection.premiumFolderData?.numSubscribers}</span>{' '}
      {t('common.supporters', {
        count: currentGallection.premiumFolderData?.numSubscribers,
      })}
    </div>
  );

  const renderGallectionNameInput = () => (
    <TextInput
      id="gallection-name"
      autoComplete="off"
      validationResult={nameErrorMessage ? 'error' : undefined}
      details={nameErrorMessage}
      wrapperClassName={c(
        gallectionSidebarStyles['gallection-name'],
        showSellAsPremiumGalleryButton &&
          gallectionSidebarStyles['gallection-name_gallery_thumb_premium_flow']
      )}
      label={t('mediapopup.gallections.input.name')}
      value={currentGallection.name}
      onChange={ev => onChangeGallectionName(ev.target.value)}
      disabled={!getCanRenameGallection(currentGallection)}
      maxLength={MAX_TITLE_LENGTH}
    />
  );

  const [descriptionValue, setDescriptionValue] = useState(
    currentGallection.richDescription
  );

  const renderDescriptionTextarea = () => {
    let descriptionPlaceholder;
    if (isPaidFolder) {
      descriptionPlaceholder = t(
        'mediapopup.gallections.gallery.paid.description.placeholder'
      );
    } else if (isWatchToAccess) {
      descriptionPlaceholder = t(
        'mediapopup.gallections.gallery.watchers.description.placeholder'
      );
    }

    const isLegacyDescription =
      (typeof descriptionValue === 'string' && descriptionValue !== '') ||
      (descriptionValue?.html?.type === 'writer' &&
        descriptionValue?.html.markup !== '');

    return (
      <FormElement
        inputId={'description'}
        label={
          <InputLabelText>
            {t('mediapopup.gallections.input.description')}
          </InputLabelText>
        }
      >
        {isLegacyDescription && (
          <LegacyDescriptionEditor
            descriptionHtml={
              isPapiEditorText(descriptionValue)
                ? descriptionValue.html
                : {
                    features: '',
                    type: 'writer',
                    markup: descriptionValue as any,
                  }
            }
            onConvertToDraft={v => {
              editorApi?.current?.setContent(v);
              setDescriptionValue(v as any);
            }}
          />
        )}
        {!isLegacyDescription && (
          <GallectionEditor
            initialContent={descriptionValue as any}
            onInitialContentReady={api => (editorApi.current = api)}
            onChange={onChangeGallectionDescriptionTiptap}
            placeholder={descriptionPlaceholder}
          />
        )}
      </FormElement>
    );
  };

  return (
    <div
      className={c(
        gallectionSidebarStyles['root'],
        showSellAsPremiumGalleryButton &&
          gallectionSidebarStyles['gallery_thumb_premium_flow']
      )}
    >
      {isShowingMediaSelector && (
        <GallectionCoverSelector
          gallection={currentGallection}
          onClose={hideMediaSelector}
        />
      )}
      <FolderRibbon
        premiumFolderData={currentGallection.premiumFolderData}
        className={gallectionSidebarStyles['premium-badge']}
      />
      <div className={gallectionSidebarStyles['sidebar']}>
        {renderGallectionCoverBlock()}
        {isPaidFolder && renderSubscribersCount()}
        <div className={gallectionSidebarStyles['form']}>
          {showSellAsPremiumGalleryButton && <PremiumContent />}
          {currentGallection.premiumFolderData?.type === 'paid' && (
            <PriceField
              onChange={onChangeGallectionPrice}
              currentGallection={currentGallection}
            />
          )}
          {renderGallectionNameInput()}
          {!currentSubfolder &&
            getCanChangeGallectionDescription(currentGallection) &&
            renderDescriptionTextarea()}
        </div>
      </div>
    </div>
  );
};
GallectionSidebar.displayName = 'GallectionSidebar';

function isPapiEditorText(value: any): value is PapiEditorText {
  return value && value.html;
}

export default GallectionSidebar;
